import { useTranslation } from '@pancakeswap/localization'
import { FetchStatus } from 'config/constants/types'
import { localStorageMiddleware } from 'hooks/useSWRContract'
import { getAchievements } from 'state/achievements/helpers'
import useSWR, { KeyedMutator } from 'swr'
import useSWRImmutable from 'swr/immutable'
import { useAccount } from 'wagmi'
import { Profile } from '../types'
import { getProfile, GetProfileResponse } from './helpers'

export const useProfileForAddress = (
  address: string,
  fetchConfiguration = {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  },
): {
  profile?: Profile
  isLoading: boolean
  isFetching: boolean
  isValidating: boolean
  refresh: KeyedMutator<GetProfileResponse>
} => {
  const { data, status, mutate, isValidating } = useSWR(
    address ? [address, 'profile'] : null,
    () => getProfile(address),
    fetchConfiguration,
  )

  const { profile } = data ?? { profile: null }

  return {
    profile,
    isLoading: status !== FetchStatus.Fetched,
    isFetching: status === FetchStatus.Fetching,
    isValidating,
    refresh: mutate,
  }
}

export const useAchievementsForAddress = (address: string) => {
  const { t } = useTranslation()

  const { data, status, mutate } = useSWRImmutable(address ? [address, 'achievements'] : null, () =>
    getAchievements(address, t),
  )

  return {
    achievements: data || [],
    isFetching: status === FetchStatus.Fetching,
    refresh: mutate,
  }
}

export const useProfile = (): {
  profile?: Profile
  hasProfile: boolean
  hasActiveProfile: boolean
  isInitialized: boolean
  isLoading: boolean
  /** true when the on-chain lookup failed (getProfile returned null or SWR errored) — distinct from "no profile" */
  fetchFailed: boolean
  refresh: KeyedMutator<GetProfileResponse>
} => {
  const { address: account } = useAccount()
  const { data, status, mutate } = useSWRImmutable(account ? [account, 'profile'] : null, () => getProfile(account), {
    use: [localStorageMiddleware],
  })

  const { profile, hasRegistered } = data ?? ({ profile: null, hasRegistered: false } as GetProfileResponse)

  const isLoading = status === FetchStatus.Fetching
  const isInitialized = status === FetchStatus.Fetched || status === FetchStatus.Failed
  const hasProfile = isInitialized && hasRegistered
  const hasActiveProfile = hasProfile && profile?.isActive
  const fetchFailed = status === FetchStatus.Failed || (status === FetchStatus.Fetched && data === null)

  return { profile, hasProfile, hasActiveProfile, isInitialized, isLoading, fetchFailed, refresh: mutate }
}
